import React, { useContext } from 'react';
import { Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';

import { AppContext } from '@prisjakt/ui-app-context';

import { Panel, PanelHeader, PanelTitle } from '@prisjakt/ui/panel';
import ExpandableGrid from '@prisjakt/ui-expandable-grid';
import ProductImage from '@prisjakt/ui-product-image';
import PriceLabel from '@prisjakt/ui-price-label';
import MatchMedia from '../match-media';
import AlertButton from './components/alert-button';
import { ProductGridCard } from '@prisjakt/ui-product-card-new';
import { Column } from '@prisjakt/ui/list/variants';
import { CaptionText } from '@prisjakt/ui/text/variants';

import { productNavigation } from '../../areas/product/actions/product';
import {
  NEW_PRODUCT_CLICK,
  COMING_SOON_CLICK,
} from '../../globals/helpers/navigation-action';
import { filterCategories } from '../../areas/landing-page/components/product-recommendations/filter-categories';

import { Card, ImageAndDate, ExpectedPrice, ProductName } from './styles';

import { NewProductRecommendationsFragment, Product, Maybe } from 'src/types';

type Props = {
  newReleases?: Recommendations['newReleases'];
  preReleases?: Recommendations['preReleases'];
  personalized?: boolean;
};

type Recommendations = NonNullable<
  NewProductRecommendationsFragment['newProductRecommendations']
>;

const NewProductReleases = (props: Props) => {
  const {
    newReleases = { nodes: [] },
    preReleases = { nodes: [] },
    personalized = true,
  } = props;

  const dispatch = useDispatch();
  const { t } = useContext(AppContext);

  if (
    !(newReleases && newReleases.nodes) ||
    !(preReleases && preReleases.nodes)
  ) {
    return null;
  }

  if (newReleases.nodes.length === 0 && preReleases.nodes.length === 0) {
    return null;
  }

  const newReleasesItems = filterCategories(
    newReleases.nodes as Maybe<Product>[]
  );
  const preReleasesItems = filterCategories(
    preReleases.nodes as Maybe<Product>[]
  );

  const newReleasesCards = newReleasesItems.map((product, index) => (
    <ProductGridCard
      key={product.id}
      brand={
        product.brand && {
          logo: product.brand.logo,
          name: product.brand.name,
        }
      }
      category={
        product.category && {
          name: product.category.name,
        }
      }
      imageSrc={product.media.first}
      lazyLoadImage
      link={product.pathName}
      name={product.name}
      price={product.priceSummary.regular || product.priceSummary.alternative}
      onClick={() => {
        dispatch(productNavigation(NEW_PRODUCT_CLICK, index + 1));
      }}
    />
  ));

  return (
    <div>
      {newReleasesItems.length > 0 && (
        <Panel>
          <PanelHeader>
            <Column style={{ flex: '0 1 auto' }}>
              <PanelTitle>
                {t('landing', 'personalized-releases.title')}
              </PanelTitle>
              {personalized ? (
                <CaptionText>
                  {t('landing', 'personalized-releases.subtitle')}
                </CaptionText>
              ) : null}
            </Column>
          </PanelHeader>
          <MatchMedia
            debounce={1000}
            serverSideRender={{ tablet: false }}
            matches={[
              {
                mediaQuery: '(min-width: 640px) and (max-width: 1000px)',
                values: { tablet: true },
              },
              {
                mediaQuery: 'all',
                values: { tablet: false },
              },
            ]}
          >
            {({ tablet }: { tablet: boolean }) => (
              <div>
                <ExpandableGrid
                  limit={tablet ? 3 : 4}
                  expandText={t('common', 'button.show-more')}
                  collapseText={t('common', 'button.show-less')}
                  items={newReleasesCards}
                  cssGrid
                />
              </div>
            )}
          </MatchMedia>
        </Panel>
      )}

      {preReleasesItems.length > 0 && (
        <Panel>
          <PanelHeader>
            <PanelTitle>
              {t('landing', 'personalized-releases.pre-releases')}
            </PanelTitle>
          </PanelHeader>
          <MatchMedia
            debounce={1000}
            serverSideRender={{ tablet: false }}
            matches={[
              {
                mediaQuery: '(min-width: 640px) and (max-width: 1000px)',
                values: { tablet: true },
              },
              {
                mediaQuery: 'all',
                values: { tablet: false },
              },
            ]}
          >
            {({ tablet }: { tablet: boolean }) => (
              <div>
                <ExpandableGrid
                  limit={tablet ? 3 : 4}
                  expandText={t('common', 'button.show-more')}
                  collapseText={t('common', 'button.show-less')}
                  items={preReleasesItems.map((product, index) => (
                    <Card key={product.id}>
                      <Link
                        to={product.pathName}
                        onClick={() => {
                          dispatch(
                            productNavigation(COMING_SOON_CLICK, index + 1)
                          );
                        }}
                      >
                        <ImageAndDate>
                          <ProductImage
                            name={product.name}
                            src={product.media.first}
                          />
                          <div>
                            <span>
                              {product.releaseDate ||
                                t(
                                  'landing',
                                  'personalized-releases.pre-releases'
                                )}
                            </span>
                          </div>
                        </ImageAndDate>
                        <ProductName>{product.name}</ProductName>
                        {product.expectedReleasePrice && (
                          <ExpectedPrice>
                            {t(
                              'landing',
                              'personalized-releases.expected-price'
                            )}{' '}
                            <PriceLabel value={product.expectedReleasePrice} />
                          </ExpectedPrice>
                        )}
                      </Link>
                      <AlertButton
                        product={product}
                        text={t('product', 'alert.create-button')}
                      />
                    </Card>
                  ))}
                />
              </div>
            )}
          </MatchMedia>
        </Panel>
      )}
    </div>
  );
};

export default NewProductReleases;
